// action.js

// Utility function to generate rank key from formData
export const getRankKey = (formData) => {
  if (formData && formData.caste && formData.gender) {
    return `${formData.caste}_${formData.gender === "Male" ? "boys" : "girls"}`;
  }
  return "";
};

// Classify risk based on student and college ranks
export const classifyRisk = (studentRank, collegeRank) => {
  // Handle cases where rank data is not available
  if (!studentRank || collegeRank === "-" || collegeRank === "NA" || collegeRank === null || collegeRank === undefined) {
    return "no-data";
  }
  
  // Convert to numbers for comparison
  const numStudentRank = parseInt(studentRank);
  const numCollegeRank = parseInt(collegeRank);
  
  // Handle non-numeric values
  if (isNaN(numStudentRank) || isNaN(numCollegeRank)) {
    return "no-data";
  }
  
  const difference = numCollegeRank - numStudentRank;
  const percentDifference = (difference / numStudentRank) * 100;
  
  if (percentDifference >= 0) return "safety-safe";
  if (percentDifference >= -10) return "safety-moderate";
  return "safety-ambitious";
};

// Load college data from API
// export const loadData = async (
//   formData,
//   setColleges,
//   setFilteredColleges,
//   setLoading,
//   applyFilters
// ) => {
//   try {
//     setLoading(true);
//     const response = await fetch("http://localhost:5000/api/College", {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({
//         caste: formData.caste,
//         gender: formData.gender,
//         rank: formData.rank,
//       }),
//     });

//     if (!response.ok) {
//       throw new Error(`HTTP error! Status: ${response.status}`);
//     }

//     const data = await response.json();
//     const collegeData = data[0] || [];
//     setColleges(collegeData);
//     applyFilters("", [], [], [], collegeData);
//     setLoading(false);
//   } catch (error) {
//     console.error("Error loading college data:", error);
//     setLoading(false);
//   }
// };

export const handleSearch = (
  event,
  setSearchTerm,
  applyFilters,
  selectedPlaces,
  selectedBranches,
  selectedTypes
) => {
  const term = event.target.value.toUpperCase();
  setSearchTerm(term);
  applyFilters(term, selectedPlaces, selectedBranches, selectedTypes);
};

// Handle place filter changes - optimized for multiple selections
export const handlePlaceFilterChange = (
  place,
  selectedPlaces = [],
  setSelectedPlaces,
  applyFiltersCallback,
  searchTerm,
  selectedBranches,
  selectedTypes
) => {
  // Create a new array based on selection status
  const updatedPlaces = selectedPlaces.includes(place)
    ? selectedPlaces.filter((p) => p !== place)
    : [...selectedPlaces, place];

  // Update the state with new selection
  setSelectedPlaces(updatedPlaces);
  
  // Apply the filters immediately with updated selection
  if (applyFiltersCallback) {
    applyFiltersCallback(searchTerm, updatedPlaces, selectedBranches, selectedTypes);
  }
  
  // Return the updated array to help with debugging
  return updatedPlaces;
};

// Handle branch filter changes - optimized for multiple selections
export const handleBranchFilterChange = (
  branch,
  selectedBranches,
  setSelectedBranches,
  applyFiltersCallback,
  searchTerm,
  selectedPlaces,
  selectedTypes
) => {
  // Create a new array based on selection status
  const updatedBranches = selectedBranches.includes(branch)
    ? selectedBranches.filter((b) => b !== branch)
    : [...selectedBranches, branch];

  // Update the state with new selection
  setSelectedBranches(updatedBranches);
  
  // Apply the filters immediately with updated selection
  if (applyFiltersCallback) {
    applyFiltersCallback(searchTerm, selectedPlaces, updatedBranches, selectedTypes);
  }
  
  // Return the updated array to help with debugging
  return updatedBranches;
};

// Handle type filter changes
export const handleTypeFilterChange = (
  type,
  selectedTypes,
  setSelectedTypes,
  applyFilters,
  searchTerm,
  selectedPlaces,
  selectedBranches
) => {
  const updatedTypes = selectedTypes.includes(type)
    ? selectedTypes.filter((t) => t !== type)
    : [...selectedTypes, type];
  setSelectedTypes(updatedTypes);
  applyFilters(searchTerm, selectedPlaces, selectedBranches, updatedTypes);
};

// Add a college to the selected list
export const addCollege = (
  college,
  selectedColleges,
  setSelectedColleges,
  setIsSaved,
  isRankSorted,
  setOriginalOrder
) => {
  if (
    !selectedColleges.some(
      (c) =>
        c.inst_code === college.inst_code &&
        c.branch_code === college.branch_code
    )
  ) {
    setSelectedColleges((prev) => [...prev, college]);
    setIsSaved(false);
    if (isRankSorted) setOriginalOrder((prev) => [...prev, college]);
  }
};

// Remove a college from the selected list
export const removeCollege = (
  college,
  selectedColleges,
  setSelectedColleges,
  setIsSaved,
  isRankSorted,
  setOriginalOrder,
  originalOrder
) => {
  const filteredList = selectedColleges.filter(
    (c) =>
      !(
        c.inst_code === college.inst_code &&
        c.branch_code === college.branch_code
      )
  );
  setSelectedColleges(filteredList);
  setIsSaved(false);
  if (isRankSorted) {
    const filteredOriginal = originalOrder.filter(
      (c) =>
        !(
          c.inst_code === college.inst_code &&
          c.branch_code === college.branch_code
        )
    );
    setOriginalOrder(filteredOriginal);
  }
};

// Move a college up in the selected list
export const moveCollegeUp = (
  index,
  selectedColleges,
  setSelectedColleges,
  setIsSaved
) => {
  // Check if selectedColleges is an array and index is valid
  if (
    !Array.isArray(selectedColleges) ||
    index <= 0 ||
    selectedColleges.length < 2
  ) {
    console.error("Invalid operation: Cannot move college up", {
      selectedColleges,
      index,
    });
    return; // Exit if conditions aren't met
  }

  // Create a copy of the array
  const updatedColleges = [...selectedColleges];

  // Swap the college with the one above it
  const temp = updatedColleges[index];
  updatedColleges[index] = updatedColleges[index - 1];
  updatedColleges[index - 1] = temp;

  // Update state
  setSelectedColleges(updatedColleges);
  if (setIsSaved) setIsSaved(false);
};

// Move a college down in the selected list
export const moveCollegeDown = (
  index,
  selectedColleges,
  setSelectedColleges,
  setIsSaved
) => {
  // Check if selectedColleges is an array and index is valid
  if (
    !Array.isArray(selectedColleges) ||
    index < 0 ||
    index >= selectedColleges.length - 1
  ) {
    console.error("Invalid operation: Cannot move college down", {
      selectedColleges,
      index,
    });
    return; // Exit if conditions aren't met
  }

  // Create a copy of the array
  const updatedColleges = [...selectedColleges];

  // Swap the college with the one below it
  const temp = updatedColleges[index];
  updatedColleges[index] = updatedColleges[index + 1];
  updatedColleges[index + 1] = temp;

  // Update state
  setSelectedColleges(updatedColleges);
  if (setIsSaved) setIsSaved(false);
};

// Sort selected colleges by rank
export const sortSelectedCollegesByRank = (
  selectedColleges,
  setSelectedColleges,
  isRankSorted,
  setIsRankSorted,
  originalOrder,
  setOriginalOrder,
  setIsSaved,
  formData,
  getRankKey
) => {
  // Store current order if not already sorted
  if (!isRankSorted && selectedColleges.length > 0) {
    setOriginalOrder([...selectedColleges]);
  }

  const rankKey = getRankKey(formData);

  // If already sorted, restore original order
  if (isRankSorted) {
    setSelectedColleges(originalOrder);
    setIsRankSorted(false);
  } else {
    // Sort by rank (convert rank to number for proper sorting)
    const sortedColleges = [...selectedColleges].sort((a, b) => {
      const aRank = parseInt(a[rankKey]) || Number.MAX_SAFE_INTEGER;
      const bRank = parseInt(b[rankKey]) || Number.MAX_SAFE_INTEGER;
      return aRank - bRank;
    });

    setSelectedColleges(sortedColleges);
    setIsRankSorted(true);
  }

  // Mark as unsaved
  setIsSaved(false);
};

// Toggle place dropdown visibility
export const togglePlaceDropdown = (
  e,
  setShowPlaceDropdown,
  setShowBranchDropdown
) => {
  e.stopPropagation();
  // Don't toggle if the click is from the search input
  if (e.target.tagName === "INPUT") {
    return;
  }
  setShowPlaceDropdown((prev) => !prev);
  setShowBranchDropdown(false);
};

// Toggle branch dropdown visibility
export const toggleBranchDropdown = (
  e,
  setShowBranchDropdown,
  setShowPlaceDropdown
) => {
  e.stopPropagation();
  // Don't toggle if the click is from the search input
  if (e.target.tagName === "INPUT") {
    return;
  }
  setShowBranchDropdown((prev) => !prev);
  setShowPlaceDropdown(false);
};

// Add these new functions
export const handleSearchInputClick = (e) => {
  e.stopPropagation();
};

export const handleOutsideClick = (e, dropdownRef, setShowDropdown) => {
  if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
    setShowDropdown(false);
  }
};

// Filter colleges by safety margin
export const filterCollegesBySafety = (
  colleges,
  formData,
  safetyMargin,
  getRankKey
) => {
  if (!formData || !formData.rank || !formData.caste || !formData.gender) {
    return colleges;
  }
  const rankKey = getRankKey(formData);
  const studentRank = parseInt(formData.rank);
  const marginValue = studentRank * (safetyMargin / 100);
  const lowerBound = Math.max(1, studentRank - marginValue);
  return colleges.filter((college) => {
    const collegeRank = parseInt(college[rankKey]) || 0;
    return collegeRank && collegeRank >= lowerBound;
  });
};

// Apply all filters to colleges
export const applyFilters = (
  searchTerm,
  selectedPlaces,
  selectedBranches,
  selectedTypes,
  colleges,
  setFilteredColleges,
  submittedFormData,
  safetyMargin,
  getRankKey
) => {
  const filtered = colleges.filter((college) => {
    const rankKey = getRankKey(submittedFormData);
    const collegeRankValue = college[rankKey];

    // --- Start of Change: Rank Filtering ---
    // If the college has a numeric rank, apply the filter.
    // If the rank is "-" or "NA", it should pass this filter.
    if (collegeRankValue && /^\d+$/.test(collegeRankValue)) {
      const collegeRank = parseInt(collegeRankValue, 10);
      const studentRank = parseInt(submittedFormData.rank, 10) || 0;

      const meetsRankCriteria =
        collegeRank >= studentRank * (1 - safetyMargin / 100);

      if (!meetsRankCriteria) {
        return false;
      }
    }
    // --- End of Change: Rank Filtering ---

    const meetsSearchCriteria = searchTerm
      ? college.inst_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        college.inst_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        college.place.toLowerCase().includes(searchTerm.toLowerCase()) ||
        college.branch_code.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    const meetsPlaceCriteria =
      selectedPlaces.length === 0 || selectedPlaces.includes(college.place);

    const meetsBranchCriteria =
      selectedBranches.length === 0 ||
      selectedBranches.includes(college.branch_code);

    const meetsTypeCriteria =
      selectedTypes.length === 0 || selectedTypes.includes(college.type);

    return (
      meetsSearchCriteria &&
      meetsPlaceCriteria &&
      meetsBranchCriteria &&
      meetsTypeCriteria
    );
  });

  // --- Start of Change: Sorting ---
  // Sort the filtered results to place non-numeric ranks at the end
  const sortedResults = filtered.sort((a, b) => {
    const rankKey = getRankKey(submittedFormData);
    const rankA = a[rankKey];
    const rankB = b[rankKey];

    const isARankless = !/^\d+$/.test(rankA);
    const isBRankless = !/^\d+$/.test(rankB);

    if (isARankless && isBRankless) return 0; // Both are rankless, keep order
    if (isARankless) return 1; // A is rankless, move it to the end
    if (isBRankless) return -1; // B is rankless, move it to the end

    // Both have numeric ranks, sort them numerically
    return parseInt(rankA, 10) - parseInt(rankB, 10);
  });
  // --- End of Change: Sorting ---

  setFilteredColleges(sortedResults);
};
